import { writeWorkspace } from './workspace-write'
import type { Blocks } from '@w/content-blocks'
import type { FlashcardsFeRouterCtx } from '../router/flashcards-fe.router-ctx'
import { commitPageEdit, commitPageEdits, type CommitPageEditInput } from './commit-page-edit'
import { newPageDocument } from './page-document'
import type { SpaceResource } from '../router/spaces-resources'

export function newPageEdit(input: {
  pageId: string
  userId: string
  title: string
  translatable?: true
  blocks?: Blocks
  /** The placement: a parent page (the caller's personal root for a top-level page), or null for none. */
  parentId: string | null
  now?: Date
}): Omit<CommitPageEditInput, 'tel'> {
  const doc = newPageDocument({ title: input.title, ...(input.translatable ? { translatable: true } : {}),
    content: { kind: 'note', children: input.blocks ?? [] },
    deletedAt: null }, input.userId)
  return { pageId: input.pageId, userId: input.userId,
    parentId: input.parentId,
    now: input.now ?? new Date(), frame: doc.encodeSnapshot().bytes, fields: { title: input.title, translatable: input.translatable, deletedAt: null, content: { kind: 'note', children: input.blocks ?? [] } } }
}

export async function commitWorkspacePageEdit(
  ctx: Pick<FlashcardsFeRouterCtx, 'writes' | 'db' | 'bucket' | 'afterCommit' | 'afterLocalCommit' | 'tel'> & { spaces: {
    get(spaceId: string): Pick<SpaceResource, 'space' | 'userId' | 'queryClient'>
  } },
  spaceId: string,
  input: Omit<CommitPageEditInput, 'tel'>,
) {
  await writeWorkspace(ctx, spaceId, async () => {
    const resource = ctx.spaces.get(spaceId)
    const db = ctx.db(spaceId)
    const queryClient = resource.queryClient
    await queryClient.addMutation<void, void>(`commit-page:${input.pageId}`, {
      mutationFn: async () => { await commitPageEdit(db, { ...input, spaceId, userId: resource.userId, bucket: ctx.bucket, tel: ctx.tel }) },
    }).mutateAsync()
    // Native subscriptions invalidate the final page/card transaction. A second
    // blanket invalidation repeats every active read after that notification.
  })
  return { id: input.pageId, workspaceId: spaceId }
}

/** A cross-page move writes the destination before clearing the source. */
export async function commitWorkspacePageMove(
  ctx: Parameters<typeof commitWorkspacePageEdit>[0],
  spaceId: string,
  input: { source: Omit<CommitPageEditInput, 'tel'>; destination: Omit<CommitPageEditInput, 'tel'> },
) {
  await writeWorkspace(ctx, spaceId, () => {
    const resource = ctx.spaces.get(spaceId)
    return commitPageEdits(ctx.db(spaceId), [input.destination, input.source].map(edit => Object.assign({}, edit, {
      spaceId, userId: resource.userId, bucket: ctx.bucket, tel: ctx.tel,
    })))
  })
}
